/**
 * Privacy Policy Page
 *
 * Privacy policy and data protection information for PetMas customers.
 * Compliant with Peruvian data protection laws (Ley 29733).
 */

import { Link } from 'react-router-dom';

const LAST_UPDATED = '15 de Enero de 2024';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldIcon />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Politica de Privacidad
          </h1>
          <p className="text-lg opacity-90">
            Ultima actualizacion: {LAST_UPDATED}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Trust Banner */}
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mb-12">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                <LockIcon />
              </div>
              <div>
                <h2 className="font-bold text-green-800">Tu privacidad es importante para nosotros</h2>
                <p className="text-green-700 text-sm">
                  Cumplimos con la Ley de Proteccion de Datos Personales (Ley 29733) y
                  sus normativas complementarias.
                </p>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <nav className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-12">
            <h2 className="font-semibold text-gray-900 mb-4">Contenido</h2>
            <ol className="space-y-2 text-sm">
              <li>
                <a href="#recopilamos" className="text-primary hover:underline">1. Informacion que Recopilamos</a>
              </li>
              <li>
                <a href="#uso" className="text-primary hover:underline">2. Como Usamos tu Informacion</a>
              </li>
              <li>
                <a href="#compartimos" className="text-primary hover:underline">3. Con Quien Compartimos tu Informacion</a>
              </li>
              <li>
                <a href="#cookies" className="text-primary hover:underline">4. Cookies y Tecnologias Similares</a>
              </li>
              <li>
                <a href="#seguridad" className="text-primary hover:underline">5. Seguridad de los Datos</a>
              </li>
              <li>
                <a href="#derechos" className="text-primary hover:underline">6. Tus Derechos</a>
              </li>
              <li>
                <a href="#retencion" className="text-primary hover:underline">7. Retencion de Datos</a>
              </li>
              <li>
                <a href="#menores" className="text-primary hover:underline">8. Menores de Edad</a>
              </li>
              <li>
                <a href="#cambios" className="text-primary hover:underline">9. Cambios en esta Politica</a>
              </li>
              <li>
                <a href="#contacto" className="text-primary hover:underline">10. Contacto</a>
              </li>
            </ol>
          </nav>

          {/* Content Sections */}
          <div className="space-y-12">
            <section id="recopilamos">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Informacion que Recopilamos
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-semibold text-gray-900 mb-3">1.1 Informacion que nos proporcionas</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                  <li><strong>Datos de registro:</strong> Nombre, apellidos, correo electronico, telefono, contrasena</li>
                  <li><strong>Datos de envio:</strong> Direccion de entrega, referencias, distrito, ciudad</li>
                  <li><strong>Datos de facturacion:</strong> RUC, razon social (solo si solicitas factura)</li>
                  <li><strong>Informacion de mascotas:</strong> Tipo, nombre, edad, raza, condiciones de salud (para recomendaciones personalizadas)</li>
                  <li><strong>Comunicaciones:</strong> Mensajes que nos envias por chat, email o redes sociales</li>
                </ul>

                <h3 className="font-semibold text-gray-900 mb-3">1.2 Informacion recopilada automaticamente</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                  <li><strong>Datos del dispositivo:</strong> Tipo de navegador, sistema operativo, resolucion de pantalla</li>
                  <li><strong>Datos de navegacion:</strong> Paginas visitadas, productos vistos, tiempo en el sitio</li>
                  <li><strong>Datos de compra:</strong> Historial de pedidos, productos agregados al carrito</li>
                  <li><strong>Direccion IP:</strong> Para seguridad y prevencion de fraude</li>
                  <li><strong>Identificadores unicos:</strong> Para reconocerte en visitas posteriores</li>
                </ul>

                <h3 className="font-semibold text-gray-900 mb-3">1.3 Informacion de terceros</h3>
                <p className="text-gray-600">
                  Si inicias sesion con Google, recibimos tu nombre, email y foto de perfil
                  segun los permisos que autorices.
                </p>
              </div>
            </section>

            <section id="uso">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Como Usamos tu Informacion
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">Utilizamos tu informacion para:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">🛒 Procesar pedidos</h4>
                    <p className="text-gray-600 text-sm">Gestionar compras, envios, pagos y comprobantes.</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">💬 Comunicarnos contigo</h4>
                    <p className="text-gray-600 text-sm">Enviar confirmaciones, actualizaciones de envio y soporte.</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">🎯 Personalizar experiencia</h4>
                    <p className="text-gray-600 text-sm">Recomendaciones de productos basadas en tus preferencias.</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">🤖 Mejorar nuestro servicio</h4>
                    <p className="text-gray-600 text-sm">Entrenar nuestro asistente de IA para darte mejores respuestas.</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">📊 Analisis y estadisticas</h4>
                    <p className="text-gray-600 text-sm">Entender como usas el sitio para mejorarlo.</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">🔒 Seguridad</h4>
                    <p className="text-gray-600 text-sm">Prevenir fraudes y proteger tu cuenta.</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="compartimos">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. Con Quien Compartimos tu Informacion
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  <strong>No vendemos tu informacion personal.</strong> Solo la compartimos cuando es
                  necesario para brindarte nuestros servicios:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <TruckIcon />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Empresas de envio</h4>
                      <p className="text-gray-600 text-sm">Compartimos tu direccion y telefono para la entrega de pedidos.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CreditCardIcon />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Procesadores de pago</h4>
                      <p className="text-gray-600 text-sm">Para procesar tus pagos de forma segura (no almacenamos datos de tarjetas).</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ServerIcon />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Proveedores de tecnologia</h4>
                      <p className="text-gray-600 text-sm">Servicios de hosting, email y analisis que nos ayudan a operar.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <BalanceIcon />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Autoridades</h4>
                      <p className="text-gray-600 text-sm">Cuando sea requerido por ley o para proteger nuestros derechos.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </section>

            <section id="cookies">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Cookies y Tecnologias Similares
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">Utilizamos cookies y tecnologias similares para:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 pr-4 font-semibold text-gray-900">Tipo</th>
                        <th className="text-left py-3 pr-4 font-semibold text-gray-900">Proposito</th>
                        <th className="text-left py-3 font-semibold text-gray-900">Duracion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="py-3 pr-4 text-gray-700">Esenciales</td>
                        <td className="py-3 pr-4 text-gray-600">Carrito de compras, sesion de usuario</td>
                        <td className="py-3 text-gray-600">Sesion</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 text-gray-700">Funcionales</td>
                        <td className="py-3 pr-4 text-gray-600">Recordar preferencias, idioma</td>
                        <td className="py-3 text-gray-600">1 año</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 text-gray-700">Analiticas</td>
                        <td className="py-3 pr-4 text-gray-600">Google Analytics, estadisticas de uso</td>
                        <td className="py-3 text-gray-600">2 años</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 text-gray-700">Marketing</td>
                        <td className="py-3 pr-4 text-gray-600">Publicidad personalizada (opcional)</td>
                        <td className="py-3 text-gray-600">30 dias</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-gray-600 mt-4 text-sm">
                  Puedes gestionar tus preferencias de cookies en la configuracion de tu navegador.
                </p>
              </div>
            </section>

            <section id="seguridad">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Seguridad de los Datos
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Implementamos medidas de seguridad para proteger tu informacion:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Encriptacion SSL/TLS de 256 bits en todas las comunicaciones</li>
                  <li>Contrasenas almacenadas con hash seguro (bcrypt)</li>
                  <li>Acceso restringido a datos personales (solo personal autorizado)</li>
                  <li>Monitoreo continuo de vulnerabilidades</li>
                  <li>Copias de seguridad encriptadas</li>
                  <li>Autenticacion de dos factores disponible</li>
                </ul>
              </div>
            </section>

            <section id="derechos">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Tus Derechos
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Conforme a la Ley 29733, tienes derecho a:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Acceso</h4>
                    <p className="text-gray-600 text-sm">Solicitar una copia de tus datos personales.</p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Rectificacion</h4>
                    <p className="text-gray-600 text-sm">Corregir datos inexactos o incompletos.</p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Cancelacion</h4>
                    <p className="text-gray-600 text-sm">Solicitar la eliminacion de tus datos.</p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Oposicion</h4>
                    <p className="text-gray-600 text-sm">Oponerte al tratamiento de tus datos.</p>
                  </div>
                </div>
                <p className="text-gray-600 mt-4">
                  Para ejercer estos derechos, contactanos a{' '}
                  <a href="mailto:privacidad@petmas.pe" className="text-primary hover:underline">
                    privacidad@petmas.pe
                  </a>
                </p>
              </div>
            </section>

            <section id="retencion">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                7. Retencion de Datos
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">Conservamos tus datos por los siguientes periodos:</p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li><strong>Cuenta activa:</strong> Mientras mantengas tu cuenta con nosotros</li>
                  <li><strong>Historial de compras:</strong> 7 años (requisito tributario SUNAT)</li>
                  <li><strong>Comunicaciones:</strong> 2 años despues del ultimo contacto</li>
                  <li><strong>Cookies de analisis:</strong> Maximo 2 años</li>
                </ul>
                <p className="text-gray-600 mt-4">
                  Despues de estos periodos, tus datos son eliminados o anonimizados.
                </p>
              </div>
            </section>

            <section id="menores">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                8. Menores de Edad
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600">
                  Nuestros servicios estan dirigidos a mayores de 18 años. No recopilamos
                  intencionalmente informacion de menores de edad. Si eres padre o tutor y
                  crees que tu hijo nos ha proporcionado informacion personal, contactanos
                  para eliminarla.
                </p>
              </div>
            </section>

            <section id="cambios">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                9. Cambios en esta Politica
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600">
                  Podemos actualizar esta politica periodicamente. Te notificaremos de cambios
                  significativos por email o mediante un aviso destacado en nuestro sitio web.
                  Te recomendamos revisar esta pagina regularmente.
                </p>
              </div>
            </section>

            <section id="contacto">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                10. Contacto
              </h2>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <p className="text-gray-600 mb-4">
                  Para consultas sobre esta politica o ejercer tus derechos ARCO:
                </p>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Responsable de Datos:</strong> PETMAS S.A.C.</p>
                  <p><strong>Email:</strong> privacidad@petmas.pe</p>
                  <p><strong>Telefono:</strong> (01) 444-5555</p>
                  <p><strong>Direccion:</strong> Lima, Peru</p>
                </div>
                <p className="text-gray-600 mt-4 text-sm">
                  Responderemos a tu solicitud en un plazo maximo de 10 dias habiles.
                </p>
              </div>
            </section>
          </div>

          {/* CTA */}
          <div className="bg-gray-50 rounded-2xl p-8 text-center mt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              ¿Tienes preguntas sobre tu privacidad?
            </h3>
            <p className="text-gray-600 mb-6">
              Estamos aqui para ayudarte.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:privacidad@petmas.pe"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
              >
                Contactar a Privacidad
              </a>
              <Link
                to="/terminos"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Ver Terminos y Condiciones
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function ShieldIcon() {
  return (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

function ServerIcon() {
  return (
    <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
    </svg>
  );
}

function BalanceIcon() {
  return (
    <svg className="w-4 h-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  );
}
